<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createUloztoConfig } from "./fs";
  import { makeFileSystemUlozto, testUloztoConnection } from "./fs-ulozto";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createUloztoConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.appToken.trim() != "" && config.username.trim() != "" && config.password != "" && !testingConnection,
  );

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing uloz.to connection");
    try {
      config.appToken = config.appToken.trim();
      config.username = config.username.trim();
      config.name = config.name.trim();
      await testUloztoConnection(config);
      if (config.name == "") {
        config.name = config.username;
      }
      let fs = makeFileSystemUlozto(config);
      let who = config.username;
      telegramNotify(`New Uloz.to connection for ${who}`);
      captureEvent("ulozto-login", {});
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Uloz.to">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-ulozto-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-ulozto-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-ulozto-token">App token:</label>
      <input
        type="password"
        id="input-ulozto-token"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.appToken}
      />

      <label class="mt-3" for="input-ulozto-user">User name:</label>
      <input
        type="text"
        id="input-ulozto-user"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.username}
      />

      <label class="mt-3" for="input-ulozto-password">Password:</label>
      <input
        type="password"
        id="input-ulozto-password"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.password}
      />

      <div class="mt-3 w-72 text-gray-500">
        The app token identifies an application to Uloz.to: you get one from Uloz.to. The user name and the password are
        the ones you sign in to Uloz.to with.
        <a href="/doc/Uloz-to" class="text-blue-600 underline" target="_blank">More</a>
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Uloz.to</button>
    </div>
  {/snippet}
</DialogBase>
